import { SurveyFormState } from '../types';
import { QUOTA_CATEGORY } from '../constants/survey-quotas.constants';

export interface SurveyQuotaSupplier {
  currentTarget: number; // current_target
  completeNeeded: number; // complete_needed
}

/**
 * Fields returned by the update-current-target API. Shared by SurveyQuota
 * (as a base) and SurveyQuotaUpdate (as the whole shape) so the two can't
 * drift out of sync.
 */
export interface QuotaFieldedMetrics {
  psQuotaId: string; // ps_quota_id
  locked: boolean; // locked
  fielded: number; // achieved
  goal: number; // current_target
  currentlyOpen: number; // currently_open
  maximum: number; // maximum
  allocationPercentage: number; // percentage
  suppliers: SurveyQuotaSupplier[]; // supplier-level distribution
}

export interface SurveyQuota extends QuotaFieldedMetrics {
  id: string; // buyer_quota_id
  label: string; // quota_title || criteria condition names
  groupKey: string; // group_key
  hasInField: boolean; // set from in_field
  isUnknown: boolean; // isUnknown
  criteria: QuotaCriteria[];
}

export interface IndividualQuotaAllocation {
  quantity: number;
  percentage: number;
  min: number;
  max: number;
}

export interface IndividualQuota {
  allocation: IndividualQuotaAllocation;
  criteria: QuotaCriteria[];
}

/**
 * The subset of a quota refreshed from the update-current-target API response.
 * Merged into the existing quota so grouping metadata (label/groupKey) is kept.
 */
export type SurveyQuotaUpdate = QuotaFieldedMetrics;

/** The user's choice in the pro-rata confirmation drawer. */
export type ProRataDecision = 'update-suppliers' | 'leave-as-is';

export const UNKNOWN_QUOTA_GROUP_KEY = 'Unknown';

/** A single-field patch keyed by psQuotaId. */
export type QuotaPatch<TField extends string, TValue> = {
  psQuotaId: string;
} & Record<TField, TValue>;

export type QuotaLockChange = QuotaPatch<'locked', boolean>;
export type QuotaCurrentTargetChange = QuotaPatch<'currentTarget', number>;

export interface QuotaFlexible {
  enable: boolean;
  value: number;
}

export interface QuotaRangeSet {
  from: number;
  to: number;
  units: number;
}

export type SurveyQuotaCategory =
  (typeof QUOTA_CATEGORY)[keyof typeof QUOTA_CATEGORY];

export interface QuotaCriteria {
  qualificationCode: number;
  conditionCodes: string[];
  conditionNames: string[];
  qualificationName: string;
  originalMaximum: number;
  originalMinimum: number;
  originalPercentage: number;
  originalRequiredCount: number;
  originalNumber: number;
  originalFlex: QuotaFlexible;
  // Range-based criteria (e.g. age, income) carry their bounds here instead of discrete
  // conditionCodes/conditionNames — mirrors the DTO's range_sets, unmapped until now.
  rangeMin?: number;
  rangeMax?: number;
  rangeUnit?: string;
  rangeSets?: QuotaRangeSet[];
  // Only ever present on the 232 (child-age-gender) criterion — mirrors QuotaCriterionDto.child_age_unit.
  childAgeUnit?: number;
}

export interface Quota {
  buyerQuotaId: string;
  quotaTitle: string;
  groupKey: string;
  requiredCount: number;
  flexible: QuotaFlexible;
  criteria: QuotaCriteria[];
  percentage: number;
  maximum: number;
  minimum: number;
  quotaCategory: SurveyQuotaCategory;
  currentTarget: number;
  fielded: number;
  individualQuotas: IndividualQuota[];
}

export type SurveyQuotasFormState = SurveyFormState<Quota[]>;

// One qualification's own committed quotas, kept by SurveyQuotasManagerService independently of
// SurveyQualification — quotas are Quota Manager's own state, not a field on the qualification.
export interface QualificationQuotas {
  qualificationCode: number;
  qualificationName: string;
  quotas: readonly Quota[];
}
